// Klartext für einen Job, der gerade WARTET statt zu arbeiten.
//
// Anlass (Pfannenshop 22.09.2026, Variantengruppe 128-EVO): Der Betreiber klickte „In VARIO
// anlegen" und sah zweiundzwanzig Minuten lang einen Spinner. Tatsächlich hatte VARIO den
// Kombinationsauftrag längst angenommen und legte nur nichts an. Die Information stand die ganze
// Zeit in der Job-Zeile (`error` = „deferred: …", Vorgangsnummer in `progress`), wurde aber
// nirgends angezeigt. Sein Urteil dazu, und es stimmt: „2 Minuten Wartezeit ist zu lange für eine
// manuelle Übernahme. Das wird kein Kunde so akzeptieren."
//
// Die Wartezeit selbst können wir nicht abkürzen, sie liegt bei VARIO. Was wir können: sagen,
// worauf gewartet wird, seit wann, und unter welcher Vorgangsnummer. Ein Anwender, der das liest,
// weiß, dass er weggehen kann, statt das Fenster zu bewachen.
//
// Reine Funktion ohne DOM, damit sie testbar bleibt (die Frontend-Tests laufen ohne jsdom).

/** Die Felder einer `account.jobs`-Zeile, die für den Wartetext gebraucht werden. */
export interface WartendeJobZeile {
  status?: string | null;
  error?: string | null;
  progress?: unknown;
}

/** Der Präfix, den der Worker beim Zurückstellen in `jobs.error` schreibt. */
const DEFER_PRAEFIX = 'deferred: ';

/**
 * Bekannte Zurückstell-Gründe in Anwendersprache. Der Rohtext ist für den Worker-Log geschrieben
 * und nennt Interna („materialisiert", „vario_defaults discovery"); unbekannte Gründe werden
 * unverändert durchgereicht, damit ein neuer Grund sichtbar bleibt statt zu verschwinden.
 */
const GRUENDE: ReadonlyArray<{ roh: RegExp; text: string }> = [
  {
    roh: /materialisiert die Varianten-Kinder/i,
    text: 'VARIO hat den Auftrag angenommen und legt die Varianten noch an',
  },
  {
    roh: /vario_defaults discovery/i,
    text: 'Die VARIO-Vorgaben werden einmalig ermittelt',
  },
  {
    roh: /Stammdaten/i,
    text: 'Ein anderer Lauf bearbeitet gerade dieselben Variantenstammdaten',
  },
];

/** Die erste Vorgangsnummer aus `progress.variantOrder`, falls der Worker eine gemerkt hat. */
export function vorgangsnummer(progress: unknown): string | null {
  if (!progress || typeof progress !== 'object') return null;
  const order = (progress as { variantOrder?: unknown }).variantOrder;
  if (!order || typeof order !== 'object') return null;
  for (const eintrag of Object.values(order as Record<string, unknown>)) {
    if (!eintrag || typeof eintrag !== 'object') continue;
    const v = (eintrag as { vorgang?: unknown }).vorgang;
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return null;
}

/**
 * Wie lange schon gewartet wird, aus dem frühesten Wartemarker in `progress`. `null`, wenn kein
 * Marker gesetzt ist — dann wird die Dauer im Text weggelassen statt „seit 0 Sekunden" zu
 * behaupten.
 */
export function wartetSeitMs(progress: unknown, jetzt: number = Date.now()): number | null {
  if (!progress || typeof progress !== 'object') return null;
  const p = progress as { variantWaitSince?: unknown; defaultsWaitSince?: unknown };
  const roh: unknown[] = [p.defaultsWaitSince];
  if (p.variantWaitSince && typeof p.variantWaitSince === 'object') {
    roh.push(...Object.values(p.variantWaitSince as Record<string, unknown>));
  }
  const zeiten = roh
    .filter((x): x is string => typeof x === 'string' && x.length > 0)
    .map((x) => Date.parse(x))
    .filter((n) => Number.isFinite(n));
  if (zeiten.length === 0) return null;
  return Math.max(0, jetzt - Math.min(...zeiten));
}

/**
 * „40 Sekunden", „3 Minuten" — grob, weil die genaue Sekunde niemanden interessiert.
 * Unterhalb von anderthalb Minuten in Sekunden, darueber in Minuten. Eine Einzahlform braucht es
 * nicht: ab 90 Sekunden rundet die Minutenzahl immer auf mindestens 2.
 */
export function dauerText(ms: number): string {
  const sekunden = Math.round(ms / 1000);
  if (sekunden < 90) return `${sekunden} Sekunden`;
  return `${Math.round(sekunden / 60)} Minuten`;
}

/**
 * Der Satz, der im Fortschritts-Hinweis steht, solange der Job zurückgestellt ist. `null`, wenn
 * der Job normal arbeitet — dann bleibt die Anzeige, wie sie ist.
 *
 * Bewusst KEINE Zeitschwelle: Der Worker stellt erst nach seinem ersten Abfrageblock zurück (rund
 * 36 Sekunden), der Hinweis erscheint also von selbst ungefähr dann, wenn eine Bedienung anfängt,
 * sich zu wundern. Eine zusätzliche Wartefrist im Browser würde ihn nur später zeigen.
 */
export function wartehinweis(zeile: WartendeJobZeile, jetzt: number = Date.now()): string | null {
  // Ein fertiger Job wartet nicht mehr. `error` behaelt den letzten Zurueckstell-Grund auch nach
  // dem Abschluss - ohne diese Pruefung stuende der Hinweis noch am erledigten Paket.
  if (zeile.status === 'completed' || zeile.status === 'failed' || zeile.status === 'cancelled') {
    return null;
  }
  const roh = typeof zeile.error === 'string' ? zeile.error : '';
  if (!roh.startsWith(DEFER_PRAEFIX)) return null;
  const grund = roh.slice(DEFER_PRAEFIX.length).trim();
  if (grund.length === 0) return null;

  const treffer = GRUENDE.find((g) => g.roh.test(grund));
  const teile = [treffer ? treffer.text : grund];

  const gewartet = wartetSeitMs(zeile.progress, jetzt);
  if (gewartet !== null && gewartet >= 5_000) teile.push(`seit ${dauerText(gewartet)}`);

  const vorgang = vorgangsnummer(zeile.progress);
  if (vorgang) teile.push(`Vorgang ${vorgang}`);

  return `${teile.join(' · ')}. Sie können das Fenster verlassen, der Lauf geht im Hintergrund weiter.`;
}
